import { ReactNode, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectContent } from '../../../store/slices/contentSlice';
import { focusedItemName } from '../../../store/slices/editorSlice';
import { _topics, duplicateTopic, selectTopic } from '../../../store/slices/topicSlice';
import { SOCIAL_SHARING } from '../../../util/constants';
import { downloadImage } from '../../../util/downloadQrcode';
import { ViiisionContextMenu } from '../../Dashboard/CardComponents/Menu/ViiisionContextMenu';

export type PageContextMenuProps = {
	children: ReactNode;
	topicId: string;
	disableDelete: boolean;
	disableDown: boolean;
	disableUp: boolean;
	editPageName: () => void;
	deletePage: () => void;
	copyPageLink: () => void;
	moveUp: () => void;
	moveDown: () => void;
};

export const PageContextMenu = (props: PageContextMenuProps) => {
	const {
		children,
		topicId,
		disableDelete,
		disableDown,
		disableUp,
		editPageName,
		copyPageLink,
		deletePage,
		moveUp,
		moveDown,
	} = {
		...props,
	};

	const dispatch = useAppDispatch();
	const duplicateTopicBinder = bindActionCreators(duplicateTopic, dispatch);

	const onSelectContent = bindActionCreators(selectContent, dispatch);
	const setTopic = bindActionCreators(selectTopic, dispatch);
	const onFocusedItemName = bindActionCreators(focusedItemName, dispatch);
	const socialSharing = () => {
		onSelectContent('');
		setTopic(topicId);
		onFocusedItemName(SOCIAL_SHARING);
	};

	const topics = useAppSelector(_topics);
	const selectedTopic = topics[topicId];
	const topicReady = !!selectedTopic?.name?.length;
	const topicName = selectedTopic?.name?.[0]?.name ?? '';

	const options = useMemo(() => {
		const baseOptions = [
			{ text: 'Copy Page Link', handleClick: copyPageLink },
			{ text: 'Page Settings', handleClick: socialSharing },
			{
				text: 'Download PNG',
				handleClick: () => downloadImage(topicId, topicName, 'png'),
			},
			{
				text: 'Download SVG',
				handleClick: () => downloadImage(topicId, topicName, 'svg'),
			},
			{ text: 'Rename', handleClick: editPageName },
			{
				text: 'Delete',
				disabled: disableDelete,
				handleClick: deletePage,
			},
			{
				text: 'Move Up',
				disabled: disableUp,
				handleClick: moveUp,
			},
			{
				text: 'Move Down',
				disabled: disableDown,
				handleClick: moveDown,
			},
		];

		const insertIndex = 3;
		const duplicateOption = topicReady
			? { text: 'Duplicate', handleClick: () => duplicateTopicBinder(topicId) }
			: { text: 'Loading..', disabled: true, handleClick: () => {} };

		return [...baseOptions.slice(0, insertIndex), duplicateOption, ...baseOptions.slice(insertIndex)];
	}, [topicReady, topicId, topicName, copyPageLink, disableDelete, disableUp, disableDown]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<ViiisionContextMenu id={topicId} options={options}>
			{children}
		</ViiisionContextMenu>
	);
};
